// NBA Stats Models
export interface NbaStatlineModel {
  readonly id?: string;
  readonly name: string;
  readonly team: string;
  readonly matchup: string;
  readonly projMinutes: number | null;
  readonly expPoints: number | null;
  readonly exp3P: number | null;
  readonly expRebounds: number | null;
  readonly expAssists: number | null;
  readonly expSteals: number | null;
  readonly expBlocks: number | null;
  readonly expTurnovers: number | null;
  readonly expPointsReboundsAssists: number | null;
  readonly uniqueMin: number | null;
  readonly upload: string;
}

// NFL Stats Models
export interface NflPassingStatsModel {
  readonly id?: string;
  readonly name: string;
  readonly team: string;
  readonly opponent: string;
  readonly attempts: number | null;
  readonly completions: number | null;
  readonly passYd: number | null;
  readonly touchdowns: number | null;
  readonly fumL: number | null;
  readonly interceptions: number | null;
}

export interface NflReceivingStatsModel {
  readonly id?: string;
  readonly name: string;
  readonly team: string;
  readonly opponent: string;
  readonly targetPercent: number | null;
  readonly catchPercent: number | null;
  readonly targetsBase: number | null;
  readonly catchBase: number | null;
  readonly ypc: number | null;
  readonly targets: number | null;
  readonly catches: number | null;
  readonly recYd: number | null;
  readonly recTD: number | null;
  readonly fumbles: number | null;
}

export interface NflRushingStatsModel {
  readonly id?: string;
  readonly team: string;
  readonly name: string;
  readonly opponent: string;
  readonly rush: number | null;
  readonly fumL: number | null;
  readonly rushYd: number | null;
  readonly rushTD: number | null;
}

// NHL Stats Models
export interface NhlSkaterStatsModel {
  readonly id?: string;
  readonly name: string;
  readonly team: string;
  readonly minutes: number | null;
  readonly shots: number | null;
  readonly goals: number | null;
  readonly assists: number | null;
  readonly blocks: number | null;
}

export interface NhlGoalieStatsModel {
  readonly id?: string;
  readonly name: string;
  readonly projectedSaves: number | null;
  readonly projectedGoals: number | null;
  readonly shutoutPercent: number | null;
}

// MMA Stats Models
export interface MmaTopStacksModel {
  readonly id?: string;
  readonly name: string;
  readonly fight: number | null;
  readonly projection: number | null;
  readonly rounds: number | null;
  readonly averageLength: number | null;
  readonly fightProjection: number | null;
  readonly salary: number | null;
  readonly winOdds: number | null;
  readonly koWin: number | null;
  readonly ownership: number | null;
  readonly pace: number | null;
  readonly topPercent: number | null;
  readonly over100FptsPercent: number | null;
  // FD-specific fields
  readonly topScorer: number | null;
  readonly mvpOwn: number | null;
  // Additional MMA fields
  readonly fightLength: number | null;
  readonly projectedFightTotal: number | null;
  readonly greaterThan100Percent: number | null;
  readonly koWinPercent: number | null;
}

// MLB Top Stacks Model
export interface MlbTopStacksModel {
  readonly id?: string;
  readonly team: string;
  readonly matchup: string;
  readonly oppSp: string;
  readonly oppSpOwn: number | null;
  readonly teamTotal: number | null;
  readonly topStackPercent: number | null;
  readonly topValuePercent: number | null;
  readonly ownershipShare: number | null;
  readonly leverage: number | null;
  readonly stackScore: number | null;
}

// MLB Projections Model
export interface MlbProjectionsModel {
  readonly id?: string;
  readonly order: number | null;
  readonly confirmed: string | null;
  readonly name: string;
  readonly hand: string | null;
  readonly fpts: number | null;
  readonly position: string | null;
  readonly team: string;
  readonly teamTotal: number | null;
  readonly postponement: number | null;
  readonly salary: number | null;
  readonly hrPercent: number | null;
  readonly value: number | null;
  readonly slate: string | null;
  readonly ks: number | null;
  readonly rosterPos: string | null;
  readonly stdev: number | null;
}

// Top Stacks Models (for NFL/NHL)
export interface NflTopStacksModel {
  readonly id?: string;
  readonly team: string;
  readonly opp: string;
  readonly qbOwn: number | null;
  readonly topStack: number | null;
  readonly avgSal: number | null;
  readonly topValue: number | null;
  readonly rushPercent: number | null;
  readonly rating: number | null;
}

export interface NhlTopStacksModel {
  readonly id?: string;
  readonly team: string;
  readonly line: number | null;
  readonly p1: string;
  readonly p2: string;
  readonly p3: string;
  readonly lineSalary: number | null;
  readonly top2StackPercent: number | null;
  readonly ownershipShare: number | null;
  readonly leverage: number | null;
}

// CFB Stats Models
export interface CfbProjectionsModel {
  readonly id?: string;
  readonly name: string;
  readonly team: string;
  readonly position: string;
  readonly salary: number | null;
  readonly projection: number | null;
  readonly attempts: number | null;
  readonly completions: number | null;
  readonly passYd: number | null;
  readonly passTD: number | null;
  readonly interceptions: number | null;
  readonly rush: number | null;
  readonly rushYd: number | null;
  readonly rushTD: number | null;
  readonly targets: number | null;
  readonly catches: number | null;
  readonly recYd: number | null;
  readonly recTD: number | null;
  readonly fumL: number | null;
  readonly value: number | null;
}

// NBA Projections Model
export interface NbaProjectionsModel {
  readonly id?: string;
  readonly name: string;
  readonly team: string;
  readonly opponent: string;
  readonly position: string;
  readonly fpts: number | null;
  readonly minutes: number | null;
  readonly salary: number | null;
  readonly injuryStatus: string | null;
  readonly starting: string | null;
  readonly confirmed: string | null;
}

// PGA Weather Model
export interface PgaWeatherModel {
  readonly id?: string;
  readonly name: string;
  readonly dkSalary: number | null;
  readonly sgWeather: number | null;
  readonly r1TeeTime: string | null;
  readonly r2TeeTime: string | null;
  readonly r1AvgWindSpeed: number | null;
  readonly r2AvgWindSpeed: number | null;
  readonly r1StrokesGained: number | null;
  readonly r2StrokesGained: number | null;
}

// Union type for all stats data
export type StatsModel =
  | NbaStatlineModel
  | NbaProjectionsModel
  | NflPassingStatsModel
  | NflReceivingStatsModel
  | NflRushingStatsModel
  | NhlSkaterStatsModel
  | NhlGoalieStatsModel
  | MmaTopStacksModel
  | NflTopStacksModel
  | NhlTopStacksModel
  | MlbProjectionsModel
  | MlbTopStacksModel
  | CfbProjectionsModel
  | PgaWeatherModel;

// Sport/Type configuration
export interface StatTypeOption {
  readonly value: string;
  readonly label: string;
}

export interface SportTypeConfig {
  readonly sport: string;
  readonly types: StatTypeOption[];
}

export const SPORT_TYPE_OPTIONS: SportTypeConfig[] = [
  {
    sport: 'NBA',
    types: [
      { value: 'statlines', label: 'Statlines' },
    ],
  },
  {
    sport: 'NFL',
    types: [
      { value: 'passing', label: 'Passing' },
      { value: 'receiving', label: 'Receiving' },
      { value: 'rushing', label: 'Rushing' },
    ],
  },
  {
    sport: 'NHL',
    types: [
      { value: 'skater', label: 'Skater' },
      { value: 'goalie', label: 'Goalie' },
    ],
  },
  {
    sport: 'CFB',
    types: [{ value: 'projections', label: 'Stats' }],
  },
];
